import {
    Button,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useEventBatching} from '../../api/events';
import {APIResponseType, CartItem, OrderInfoByStatus, OrderStatus} from '../../api/models';
import {useOrderInfoDetails} from '../../api/order';
import {ItemDetailsModal} from './components/ItemDetailsModal';
import OrderStatusMenu from './components/OrderStatusMenu';
import { convertPaiseToRupees } from '../../common/utils';

const StyledTableCell = styled(TableCell)(({}) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#15171c',
        color: '#fff',
        textAlign: 'center',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
    },
}));

const StyledTableRow = styled(TableRow)(({}) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '$8e8e8e',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const OrderStatusTitle = ({orderStatus}: {orderStatus: string}) => {
    const getOrderStatusTitle = (orderStatus: string): string => {
        switch (orderStatus) {
            case OrderStatus.CREATED:
                return 'Newly Created Orders';
            case OrderStatus.PAYMENT_CONFIRMED:
                return 'Payment Confirmed Orders';
            case OrderStatus.ORDER_CONFIRMED:
                return 'Confirmed Orders';
            case OrderStatus.ORDER_DECLINED:
                return 'Orders Declined';
            case OrderStatus.ORDER_COMPLETED:
                return 'Orders Completed';
            case OrderStatus.PAYMENT_REFUNDED:
                return 'Payments Refunded';
            default:
                return '';
        }
    };

    return (
        <h3
            style={{
                fontSize: '1.5rem',
                fontWeight: 500,
                marginTop: 2,
                marginBottom: 2,
                letterSpacing: 1,
            }}>
            {getOrderStatusTitle(orderStatus)}
        </h3>
    );
};

export const OrderDashboardScreen = () => {
    const {trackEvent} = useEventBatching();
    const [open, setOpen] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<CartItem[]>([]);

    const handleOpen = (item: CartItem[]) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedItem([]);
    };

    const {data, refetch, isLoading, error} = useOrderInfoDetails();

    useEffect(() => {
        if (error) {
            trackEvent({
                eventName: 'Fetch Order List: Failed',
                props: {
                    errorName: error.name,
                    errorMessage: error.message,
                    errorStack: error.stack ? JSON.stringify(error.stack) : '',
                },
            });
        }
    }, [error]);

    let el = <></>;
    if (data) {
        if (data.type === APIResponseType.OrderInfoByStatus) {
            const orders = (data.data as OrderInfoByStatus).orders;
            el = (
                <>
                    {Object.entries(orders).map(([orderStatus, orderInfoList]) => {
                        return (
                            <div>
                                <TableContainer
                                    key={orderStatus}
                                    component={Paper}
                                    sx={{marginTop: 4, marginBottom: 4, padding: 4}}>
                                    <OrderStatusTitle orderStatus={orderStatus} />
                                    <Table sx={{minWidth: 700}} aria-label={`${orderStatus} orders table`}>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>Order Id</StyledTableCell>
                                                <StyledTableCell>Owner VPA</StyledTableCell>
                                                <StyledTableCell>Customer VPA</StyledTableCell>
                                                <StyledTableCell>Transaction Id</StyledTableCell>
                                                <StyledTableCell>Final Amount</StyledTableCell>
                                                <StyledTableCell>Actions</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderInfoList.map((order) => (
                                                <StyledTableRow key={order.id}>
                                                    <StyledTableCell>{order.id}</StyledTableCell>
                                                    <StyledTableCell>{order.ownerVPA}</StyledTableCell>
                                                    <StyledTableCell>{order.customerVPA}</StyledTableCell>
                                                    <StyledTableCell>{order.transactionID}</StyledTableCell>
                                                    <StyledTableCell>
                                                        {convertPaiseToRupees(order.paymentDetails.finalAmount)}
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <OrderStatusMenu
                                                            orderStatus={orderStatus as OrderStatus}
                                                            order={order}
                                                            reloadOrderDetails={refetch}
                                                        />
                                                        <Button onClick={() => handleOpen(order.items)}>
                                                            View Items
                                                        </Button>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <ItemDetailsModal open={open} cartItems={selectedItem} onClose={handleClose} />
                            </div>
                        );
                    })}
                </>
            );
        } else {
            /**
             * Have this replaced with an error component
             */
        }
    } else if (isLoading) {
        /**
         * make this a generic loading state component
         */
        el = (
            <>
                <h3>Have a loading state here</h3>
            </>
        );
    } else if (error) {
        /**
         * Have this replaced with an error component
         */
        el = (
            <>
                <h3>{error.name}</h3>
                <p>{error.message}</p>
            </>
        );
    }
    return <div style={{marginTop: 75}}>{el}</div>;
};
